import invariant from 'invariant';

import { NO_FILTER_SENTINEL } from 'utils/constants';
import {
  filterNulls,
  isObject,
  mapObject,
  objectEmpty,
  remove,
  setEquals,
  updateObject,
  values,
} from 'utils/functional';

type AllSearchTypes = StaffTypeFilter | ListingType | 'CMS_PAGE';
export const searchableFacetsForType = (
  type: AllSearchTypes
): Array<string> => {
  // @ts-expect-error ts-migrate(2740) FIXME: Type '{ ALL: undefined[]; JOB: string[]; INTERNSHI... Remove this comment to see the full error message
  const byType: Record<AllSearchTypes, Array<string>> = {
    ALL: [],
    JOB: ['orgAreasOfFocusFacet', 'jobKeywordsFacet'],
    INTERNSHIP: ['orgAreasOfFocusFacet', 'internshipKeywordsFacet'],
    VOLOP: ['volopAreasOfFocusFacet', 'volopKeywordsFacet'],
    ORG: ['orgAreasOfFocusFacet'],
    IDEALIST_DAY_EVENT: ['idealistDayEventAreasOfFocusFacet'],
  };
  return byType[type] || [];
};
export const sideFacetsForType = (
  searchFacets: Readonly<Record<string, SearchFacet>>,
  type: AllSearchTypes
): Array<SearchFacet> => {
  // @ts-expect-error ts-migrate(2739) FIXME: Type '{ ALL: undefined[]; JOB: string[]; INTERNSHI... Remove this comment to see the full error message
  const facetTypeToKeys: Record<AllSearchTypes, Array<string>> = {
    ALL: [],
    JOB: [
      'listingLocationTypeFacet',
      'jobKeywordsFacet',
      'orgAreasOfFocusFacet',
      'educationFacet',
      'professionalLevelFacet',
      'employmentTypeFacet',
      'orgTypeFacet',
      'statusFacet',
      'localeFacet',
    ],
    INTERNSHIP: [
      'listingLocationTypeFacet',
      'internshipKeywordsFacet',
      'orgAreasOfFocusFacet',
      'orgTypeFacet',
      'paymentFacet',
      'statusFacet',
      'localeFacet',
    ],
    ORG: [
      'orgAreasOfFocusFacet',
      'orgOrgTypeFacet',
      'statusFacet',
      'localeFacet',
    ],
    GROUP: ['statusFacet', 'groupAreasOfFocusFacet', 'localeFacet'],
    IDEALIST_DAY_EVENT: [
      'statusFacet',
      'localeFacet',
      'idealistDayEventAreasOfFocusFacet',
    ],
    // Anything added for actions must also be added in VolopSearchFiltersSidebarContainer.
    VOLOP: [
      'volopAreasOfFocusFacet',
      'volopKeywordsFacet',
      'listingLocationTypeFacet',
      'isCovidFacet',
      'hasLocationFacet',
      'actionTypeFacet',
      'actionDateFacet',
      'volopSourceFacet', // Not shown in sidebar.
      'localeFacet',
    ],
    GRAD_PROGRAM: [
      'publicPrivateFacet',
      'statusFacet',
      'gradAreasOfStudyFacet',
    ],
  };
  const facetKeys = facetTypeToKeys[type] || [];
  const includedFilters = facetKeys
    .filter((key) => searchFacets[key])
    .map((key) => searchFacets[key]);
  return includedFilters;
};

// stateSearchFilters takes the redux filter state and
// outputs an "almost" saved-search-serializable data structure
export function stateSearchFilters(
  searchFacets: Record<string, SearchFacet>,
  facetFilters: SearchFilters | SearchFiltersWithType<AllSearchTypes>
) {
  let filters = {} as SearchFiltersWithType<AllSearchTypes>;

  if (facetFilters && facetFilters.type !== 'ALL') {
    filters.type = facetFilters.type;
  }

  values(searchFacets).forEach((facet) => {
    const setting = facetFilters[facet.id];

    if (facet.type === 'boolean') {
      if (setting === 'YES') filters[facet.id] = true;
      else if (setting === 'NO') filters[facet.id] = false; // take no action for 'ALL'
    } else if (facet.type === 'array' || facet.type === 'string') {
      // Check if the array has all options in it aswell
      if (!setting || setting.length === 0) return;
      const allOptionsSelected = facet.options.every(
        (i) => Array.isArray(setting) && setting.includes(i)
      );
      if (!allOptionsSelected) filters[facet.id] = setting;
    } else if (facet.type === 'date') {
      const s = setting as Record<string, number>;

      if (!objectEmpty(s)) {
        filters = {
          ...filters,
          [facet.id]: s,
        };
      }
    } else {
      throw new Error(`invalid facet type: ${facet.type}`);
    }
  });
  return filters;
}
// serializeFiltersForSavedSearch takes the redux filter state and outputs
// a saved-search-serializable data structure. See `stateSearchFilters`
export const serializeFiltersForSavedSearch = ({
  searchFacets,
  filters,
}: {
  filters: SearchFilters | SearchFiltersWithType<AllSearchTypes>;
  searchFacets: Record<string, SearchFacet>;
}) =>
  mapObject(stateSearchFilters(searchFacets, filters), (key: string, value) =>
    ['type', 'startsLT', 'endsGT'].includes(key)
      ? [key, value]
      : [searchFacets[key].name, value]
  );

export const areFiltersDefault = (
  facets: Array<SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  filters: SearchFilters
) => {
  const facetsToCheck = remove(facets, (f) =>
    facetsThatArentLoaded.includes(f.id)
  );

  const arrayCheck = (facet, filter) =>
    !filter ||
    (Array.isArray(filter) && setEquals(facet.options, filter)) ||
    filter === 'ALL';

  const dateCheck = (facet, filter) =>
    !filter ||
    objectEmpty(filter) ||
    (isObject(filter) && values(filter).every((v) => !v));

  const defaultCheck = (facet, filter) => !filter || filter === 'ALL';

  return facetsToCheck.every((f) => {
    const filter = filters[f.id];
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    return f.type === 'array'
      ? arrayCheck(f, filter)
      : f.type === 'date'
      ? dateCheck(f, filter)
      : defaultCheck(f, filter);
  });
};
export const initialFacetFilters = (
  facets: Array<SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  queryParams: Readonly<Record<string, string>>
) =>
  updateObject(
    // @ts-expect-error ts-migrate(2557) FIXME: Expected at least 1 arguments, but got 0 or more.
    ...(facets || [])
      .filter(
        (f) => !facetsThatArentLoaded.includes(f.id) || queryParams[f.name]
      )
      .map((facet) => {
        if (facet.type === 'array') {
          const paramValue = queryParams[facet.name];

          return paramValue
            ? {
                [facet.id]: Array.isArray(paramValue)
                  ? paramValue
                  : [paramValue],
              }
            : undefined;
        }

        if (facet.type === 'boolean') {
          // special handling for true/false instead of 'YES'/'NO' used for landing page cms
          // @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message
          if (queryParams[facet.name] === true) {
            return {
              [facet.id]: 'YES',
            };
          }

          // @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message
          if (queryParams[facet.name] === false) {
            return {
              [facet.id]: 'NO',
            };
          }
        }

        if (facet.type === 'date') {
          const dateParams = facet.names.reduce((acc, cur) => {
            if (queryParams[cur]) {
              // eslint-disable-next-line no-param-reassign
              acc[cur] = queryParams[cur];
            }
            return acc;
          }, {});
          return {
            [facet.id]: dateParams,
          };
        }

        return queryParams[facet.name]
          ? {
              [facet.id]: queryParams[facet.name],
            }
          : undefined;
      })
  );

export const initialFacetFiltersForPostSearch = (
  facets: Array<SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  queryParams: Readonly<Record<string, string>>
) =>
  updateObject(
    // @ts-expect-error ts-migrate(2557) FIXME: Expected at least 1 arguments, but got 0 or more.
    ...(facets || [])
      .filter(
        (f) => !facetsThatArentLoaded.includes(f.id) || queryParams[f.name]
      )
      .map((facet) => {
        if (facet.type === 'array') {
          const paramValue = queryParams[facet.name];
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          return paramValue === NO_FILTER_SENTINEL
            ? {
                [facet.id]: [],
              }
            : !paramValue
            ? {
                [facet.id]: facet.options,
              }
            : {
                [facet.id]: Array.isArray(paramValue)
                  ? paramValue
                  : [paramValue],
              };
        }

        if (facet.type === 'boolean') {
          // special handling for true/false instead of 'YES'/'NO' used for landing page cms
          // @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message
          if (queryParams[facet.name] === true) {
            return {
              [facet.id]: 'YES',
            };
          }

          // @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message
          if (queryParams[facet.name] === false) {
            return {
              [facet.id]: 'NO',
            };
          }
        }

        if (facet.type === 'date') {
          const dateParams = facet.names.reduce((acc, cur) => {
            if (queryParams[cur]) {
              // eslint-disable-next-line no-param-reassign
              acc[cur] = queryParams[cur];
            }
            return acc;
          }, {});
          return {
            [facet.id]: dateParams,
          };
        }

        return {
          [facet.id]: queryParams[facet.name] || 'ALL',
        };
      })
  );

export const initialFacetFiltersForMainSearch = (
  searchFacets: Record<string, SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  queryParams: Readonly<{
    actionType?: string;
    type: StaffTypeFilter | ListingType;
    saved?: string;
  }>,
  remote: boolean | null | undefined = false,
  reset = false
) => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line prefer-destructuring
  const type = queryParams.type;
  invariant(type, `missing type: ${JSON.stringify(queryParams)}`);
  const f = initialFacetFilters(
    sideFacetsForType(searchFacets, type),
    facetsThatArentLoaded,
    queryParams
  );
  let hasRefreshed = false;

  // @NOTE - This is kind of a hack and using a deprecated API
  // Maybe we can reconsider how we set the "ALL" filter?
  if (
    typeof window !== 'undefined' &&
    window.performance &&
    performance.navigation.type === 1
  ) {
    hasRefreshed = true;
  }

  if (remote) {
    f.listingLocationTypeFacet = ['REMOTE', 'REMOTE_TEMPORARY_COVID'];
  }

  if (!f.listingLocationTypeFacet) {
    f.listingLocationTypeFacet = ['ONSITE', 'REMOTE', 'REMOTE_TEMPORARY_COVID'];
  }

  if (
    !reset &&
    !hasRefreshed &&
    !queryParams.saved &&
    type === 'VOLOP' &&
    !queryParams.actionType
  ) {
    f.actionTypeFacet = ['VOLOP'];
  } else if (reset && type === 'VOLOP') {
    f.actionTypeFacet = ['VOLOP', 'EVENT', 'LOAN', 'DONATION', 'PETITION'];
  }

  return {
    type,
    ...f,
  };
};

// convert filter ids to filter names
// also convert YES/NO/ALL to bool (or null)
export const convertFilterIdsToNames = (
  searchFacets: Record<string, SearchFacet>,
  {
    filters,
  }: {
    filters: SearchFilters;
  }
) =>
  filterNulls<string, SearchFilters>(
    mapObject(filters, (key, value) => {
      if (key === 'type') {
        return ['type', value];
      }

      const facet = searchFacets[key];

      if (facet && facet.name) {
        if (facet.type === 'boolean') {
          if (value === 'YES') {
            return [facet.name, true];
          }

          if (value === 'NO') {
            return [facet.name, false];
          }

          return [facet.name, null];
        }

        if (facet.type === 'array') {
          const allOptionsSelected = facet.options.every(
            (i) => Array.isArray(value) && value.includes(i)
          );
          if (allOptionsSelected) {
            return [facet.name, null];
          }
        }

        return [facet.name, value];
      }

      return [key, value];
    })
  );
